/**
 * Synthesised audio in the Cache API.
 *
 * Binaries belong here rather than in IndexedDB: it is the right store for opaque blobs and it
 * shares the service-worker story with the app shell (design §6). Audio is deliberately excluded
 * from the export — it is regenerable from text and would bloat every backup — so this cache is
 * rebuilt by the pre-render pass rather than restored.
 *
 * Keys are a hash, not the text. Two reasons: no utterance text sits in a cache key where it
 * could surface in a devtools listing or a diagnostic dump, and a stable key means re-rendering a
 * phrase overwrites its audio instead of accumulating copies.
 *
 * The voice is part of the key. When the family picks a different voice the whole cache is
 * effectively invalidated, which is correct — half the phrases in one voice and half in another
 * would be worse than a re-render.
 */

const CACHE_NAME = 'sags-audio-v1';

const hex = (bytes: ArrayBuffer): string =>
  [...new Uint8Array(bytes)].map((b) => b.toString(16).padStart(2, '0')).join('');

/**
 * Stable cache key for an utterance in a voice.
 *
 * Text is normalised the way it would be spoken (collapsed whitespace, trimmed) so that
 * "Ja " and "Ja" share one entry.
 */
export async function audioKeyFor(text: string, voiceId: string): Promise<string> {
  const normalised = text.replace(/\s+/gu, ' ').trim();
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(`${voiceId}|${normalised}`),
  );
  return `/__sags-audio/v1/${voiceId}/${hex(digest).slice(0, 32)}.mp3`;
}

async function open(): Promise<Cache | null> {
  if (typeof caches === 'undefined') return null;
  try {
    return await caches.open(CACHE_NAME);
  } catch (err) {
    console.error('[audio-cache] open failed', err);
    return null;
  }
}

export async function getCachedAudio(key: string): Promise<ArrayBuffer | null> {
  const cache = await open();
  if (!cache) return null;
  try {
    const hit = await cache.match(key);
    return hit ? await hit.arrayBuffer() : null;
  } catch (err) {
    console.error('[audio-cache] match failed', err);
    return null;
  }
}

export async function putCachedAudio(key: string, bytes: ArrayBuffer): Promise<boolean> {
  const cache = await open();
  if (!cache) return false;
  try {
    await cache.put(
      key,
      new Response(bytes, {
        headers: {
          'Content-Type': 'audio/mpeg',
          'Content-Length': String(bytes.byteLength),
        },
      }),
    );
    return true;
  } catch (err) {
    // Most likely a quota refusal. Phase 0 measured 39.3 GB of quota with 36 GB free, so this
    // is not expected — but a failed cache write must never stop the utterance being spoken.
    console.error('[audio-cache] put failed', err);
    return false;
  }
}

export async function hasCachedAudio(key: string): Promise<boolean> {
  const cache = await open();
  if (!cache) return false;
  return (await cache.match(key)) !== undefined;
}

/** For the care-mode "re-render everything" action and for a voice change. */
export async function clearAudioCache(): Promise<void> {
  if (typeof caches === 'undefined') return;
  await caches.delete(CACHE_NAME);
}

export async function audioCacheSize(): Promise<number> {
  const cache = await open();
  if (!cache) return 0;
  return (await cache.keys()).length;
}
